import React from "react"
import {
  Box,
  Flex,
  FormLabel,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Switch,
} from "@chakra-ui/core"
import { Link as GatsbyLink } from "gatsby"
import { Button } from "../Buttons"
import ModalCloseButton from "../Buttons/ModalCloseButton"
import Body from "../typography/Body"
import SmallCaps from "../typography/SmallCaps"
import { bp } from "../../utils/MediaQueries"
import { ConsentState, PreferencesFocus } from "../../utils/consent"

type CookiePreferencesModalProps = {
  isOpen: boolean
  onClose: () => void
  consent: ConsentState
  gpc: boolean
  focus?: PreferencesFocus
  onSave: (consent: ConsentState) => void
}

type CategoryProps = {
  id: string
  title: string
  description: string
  isChecked: boolean
  isDisabled?: boolean
  onChange?: () => void
  note?: string
  switchRef?: React.Ref<HTMLInputElement>
}

const Category = ({
  id,
  title,
  description,
  isChecked,
  isDisabled,
  onChange,
  note,
  switchRef,
}: CategoryProps) => (
  <Box py="1rem" borderTop="1px solid" borderColor="dividerLine">
    <Flex justify="space-between" align="center" mb="0.375rem">
      <FormLabel htmlFor={id} p={0} cursor={isDisabled ? "default" : "pointer"}>
        <Body fontWeight="bold" color="night">
          {title}
        </Body>
      </FormLabel>
      <Switch
        id={id}
        ref={switchRef}
        color="primarySet"
        isChecked={isChecked}
        isDisabled={isDisabled}
        onChange={onChange}
      />
    </Flex>
    <Body size="sm" color="dawn">
      {description}
    </Body>
    {note && (
      <Body size="sm" color="dusk" mt="0.375rem" fontWeight="medium">
        {note}
      </Body>
    )}
  </Box>
)

export default function CookiePreferencesModal({
  isOpen,
  onClose,
  consent,
  gpc,
  focus,
  onSave,
}: CookiePreferencesModalProps) {
  const [draft, setDraft] = React.useState<ConsentState>(consent)
  const analyticsRef = React.useRef<HTMLInputElement>(null)
  const marketingRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (isOpen) setDraft(consent)
  }, [isOpen])

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      size="lg"
      initialFocusRef={
        focus === "marketing"
          ? marketingRef
          : focus === "analytics"
          ? analyticsRef
          : undefined
      }
    >
      <ModalOverlay />
      <ModalContent
        borderRadius="0.5rem"
        py="1.5rem"
        mx="0.5rem"
        position="relative"
      >
        <ModalCloseButton zIndex={3} onClick={onClose} />
        <ModalHeader textAlign="center" pt="0">
          <SmallCaps size="lg" fontWeight="bold">
            Cookie Preferences
          </SmallCaps>
        </ModalHeader>
        <ModalBody>
          <Body size="sm" color="dusk" mb="1rem">
            Choose which optional cookies we use. See our{" "}
            <GatsbyLink to="/pages/privacy-policy" onClick={onClose}>
              <Box as="span" textDecoration="underline">
                Privacy Policy
              </Box>
            </GatsbyLink>
            .
          </Body>
          <Category
            id="cookie-necessary"
            title="Strictly necessary"
            description="Required for checkout, your account, site security, chat (Zendesk), reviews (Trustpilot), and financing (Affirm). Always on."
            isChecked
            isDisabled
          />
          <Category
            id="cookie-analytics"
            title="Analytics"
            description="Helps us understand site traffic and improve the store."
            isChecked={draft.analytics}
            onChange={() =>
              setDraft((current) => ({
                ...current,
                analytics: !current.analytics,
              }))
            }
            switchRef={analyticsRef}
          />
          <Category
            id="cookie-marketing"
            title="Advertising"
            description="Personalized ads and marketing measurement. Turning this off opts you out of selling or sharing personal information."
            isChecked={draft.marketing && !gpc}
            isDisabled={gpc}
            onChange={() =>
              setDraft((current) => ({
                ...current,
                marketing: !current.marketing,
              }))
            }
            note={
              gpc
                ? "Global Privacy Control is on—advertising cookies stay off."
                : undefined
            }
            switchRef={marketingRef}
          />
        </ModalBody>
        <ModalFooter justifyContent="center" flexWrap="wrap" pb={0}>
          <Button
            theme="secondary"
            mr={bp(0, "1rem")}
            mb={bp("0.75rem", 0)}
            w={bp("100%", "auto")}
            onClick={() => onSave({ analytics: false, marketing: false })}
          >
            Reject all
          </Button>
          <Button
            theme="primary"
            w={bp("100%", "auto")}
            onClick={() => onSave(draft)}
          >
            Save preferences
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
